import { useEffect, useState } from "react";
import { MailWarning, X, Loader2 } from "lucide-react";
import { parseApiError } from "@/lib/api/client";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");

/** Slim, self-contained banner nudging authenticated users to verify their
 * email. Self-fetches verification status from ``/iam/me/`` so it stays
 * decoupled from the global store, and offers a one-click resend. Renders
 * nothing until it positively knows the email is unverified, so it never
 * flashes for verified users. */
export function VerifyEmailBanner() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("ckos.verifyDismissed") === "1");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("ckos.token");
    if (!token || dismissed) return;
    const slug = localStorage.getItem("ckos.tenantSchema");
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/iam/me/`, {
          headers: {
            Authorization: `Token ${token}`,
            ...(slug ? { "X-Tenant-Schema": slug } : {}),
          },
        });
        if (!r.ok) return;
        const me = await r.json();
        if (me && me.email && me.email_verified === false) {
          setEmail(me.email);
          setShow(true);
        }
      } catch { /* offline — stay hidden */ }
    })();
  }, [dismissed]);

  if (!show || dismissed) return null;

  async function resend() {
    setSending(true);
    setError(null);
    try {
      const token = localStorage.getItem("ckos.token");
      const slug = localStorage.getItem("ckos.tenantSchema");
      const r = await fetch(`${API_BASE}/iam/email/verify/request/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
          ...(slug ? { "X-Tenant-Schema": slug } : {}),
        },
        body: "{}",
      });
      if (!r.ok) throw await parseApiError(r);
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setSending(false); }
  }

  function dismiss() {
    sessionStorage.setItem("ckos.verifyDismissed", "1");
    setDismissed(true);
  }

  return (
    <div className="flex items-center gap-3 border-b border-warn/30 bg-warn/10 px-4 py-2 text-[12.5px] text-text">
      <MailWarning size={16} className="shrink-0 text-warn" />
      <span className="min-w-0 flex-1 truncate">
        Please verify <strong>{email}</strong> to secure your account.
      </span>
      {sent ? (
        <span className="shrink-0 text-good">Verification email sent.</span>
      ) : (
        <button className="btn-ghost shrink-0 text-accent" onClick={resend} disabled={sending}>
          {sending ? <Loader2 size={14} className="animate-spin" /> : "Resend email"}
        </button>
      )}
      {error && <span className="shrink-0 text-bad" title={error}>Resend failed</span>}
      <button className="btn-ghost shrink-0" onClick={dismiss} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
}
